// outputRegistry.js
// Display-only metadata for Function Output destinations. This mirrors the
// backend Output registry for UI purposes only (labels, icons, helper text, and
// the `has_external_effect` flag used when rendering confirmation prompts). The
// backend remains the sole authority on whether an Output actually has an
// external effect; every enforcement path recalculates the value from the real
// Output block documents. Nothing here authorises a send.
//
// `email` is gated behind the non-authoritative build-time flag
// REACT_APP_EMAIL_FEATURE_ENABLED. The flag hides creation/contact/tool
// affordances only. An Output block that already stores output_type 'email'
// still resolves through getOutputMeta so the builder can render it read-only if
// the kill switch is used; hiding the destination never erases existing config.

export const OUTPUT_REGISTRY = {
  decode_ui: {
    type: 'decode_ui',
    label: 'Decode UI',
    iconName: 'Dashboard',
    helperText: 'Show the selected node outputs on the Decode dashboard.',
    hasExternalEffect: false,
    featureFlagged: false,
  },
  email: {
    type: 'email',
    label: 'Email',
    iconName: 'Email',
    helperText: 'Send the selected attachments to company contacts by email.',
    hasExternalEffect: true,
    featureFlagged: true,
  },
};

// Read the flag at call time (not module load) so tests and runtime env changes
// are honoured without re-importing.
export const isEmailFeatureEnabled = () => process.env.REACT_APP_EMAIL_FEATURE_ENABLED === 'true';

// Metadata for a stored output_type, or null for an unknown type. Always returns
// email metadata regardless of the feature flag so existing Email Outputs render.
export const getOutputMeta = (outputType) => OUTPUT_REGISTRY[outputType] || null;

// Authoritative-for-display external-effect flag. Unknown types are treated as
// non-effectful for rendering; the backend still gates the actual send.
export const outputHasExternalEffect = (outputType) => Boolean(OUTPUT_REGISTRY[outputType]?.hasExternalEffect);

// Destinations a user may currently select in the builder. Feature-flagged
// destinations (email) are only offered when the flag is enabled.
export const getVisibleOutputTypes = () => Object.values(OUTPUT_REGISTRY)
  .filter((meta) => !meta.featureFlagged || isEmailFeatureEnabled());
